import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { VideoCompilerCompilation } from '@/types/resources';
import { api } from '@/utils/api';
import {
  VideoCompilerCompilationStatus,
  VideoCompilerCompilationType,
} from '@/utils/enums';

import CompilationStatus from './CompilationStatus';
import CompilationYoutubeVideos from './CompilationYoutubeVideos';
import YoutubeVideoForm from './YoutubeVideoForm';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 168px;
`;

const Tr = styled.tr`
  td:not(:last-child) {
    cursor: pointer;
  }

  &:hover {
    td:not(:last-child) {
      background: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

interface CompilationDestinationProps {
  $destination: VideoCompilerCompilationType;
}

const CompilationDestination = styled(Text)<CompilationDestinationProps>`
  padding: 2px 4px;
  border-radius: 4px;
  text-transform: capitalize;
  display: inline-block;

  ${({ theme, $destination }) =>
    $destination === VideoCompilerCompilationType.FINAL &&
    css`
      color: ${theme.colors.info};
      background: #edf5fc;
    `}

  ${({ theme, $destination }) =>
    $destination === VideoCompilerCompilationType.FINAL &&
    css`
      color: ${theme.colors.warning};
      background: ${theme.colors.warningLight};
    `}
`;

interface CompilationTypeProps {
  $compilationType: VideoCompilerCompilation['compilationType'];
}

const CompilationType = styled(Text)<CompilationTypeProps>`
  padding: 2px 4px;
  border-radius: 4px;
  text-transform: capitalize;
  display: inline-block;

  ${({ $compilationType }) =>
    $compilationType === 'compilation' &&
    css`
      color: #ad64c5;
      background: #f9f3fb;
    `}
  ${({ $compilationType }) =>
    $compilationType === 'version' &&
    css`
      color: #c9513d;
      background: #fbf1f0;
    `}
`;

interface Props {
  compilation: VideoCompilerCompilation;
}

const CompilationsTableRow: FC<Props> = ({ compilation }) => {
  const navigate = useNavigate();

  const [showYoutubeForm, setShowYoutubeForm] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to delete this compilation?',
        'This will delete all compilation information',
      );

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/video-compiler/compilations/${compilation.id}`);

      await queryClient.invalidateQueries(['video_compiler_compilations']);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  const onEdit = () =>
    navigate(`../${compilation.id}/edit`, { relative: 'path' });

  const onClick = () => navigate(`../${compilation.id}`, { relative: 'path' });

  return (
    <Tr onClick={onClick}>
      <Td>{compilation.title}</Td>
      <Td>
        <CompilationDestination
          $type="extraSmall"
          $destination={compilation.type}
        >
          {compilation.type}
        </CompilationDestination>
      </Td>
      <Td>
        <CompilationType
          $type="extraSmall"
          $compilationType={compilation.compilationType}
        >
          {compilation.compilationType}
        </CompilationType>
      </Td>
      <Td>
        <CompilationStatus
          status={compilation.status}
          progress={compilation.progress}
        />
      </Td>
      <Td>{compilation.versionsCount}</Td>
      <Td>{compilation.tipsCount}</Td>
      <Td>
        <CompilationYoutubeVideos videos={compilation.youtubeVideos!} />
      </Td>
      <Td>{compilation.user?.name ?? '-'}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <StyledActionsDropdown>
            {compilation.status === VideoCompilerCompilationStatus.DRAFT && (
              <ActionsDropdownButton as="button" onClick={onEdit}>
                <PencilIcon />
                Edit
              </ActionsDropdownButton>
            )}
            {!!compilation.video && (
              <>
                <ActionsDropdownButton
                  as="button"
                  onClick={() => setShowYoutubeForm(true)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.6657 4.14882C15.5744 3.80979 15.3958 3.50067 15.1475 3.25241C14.8992 3.00415 14.5901 2.82546 14.2511 2.73424C13.0028 2.3999 7.99992 2.3999 7.99992 2.3999C7.99992 2.3999 2.99672 2.3999 1.74892 2.73424C1.40988 2.82545 1.10075 3.00413 0.852487 3.25239C0.604226 3.50065 0.425545 3.80978 0.334336 4.14882C0 5.39662 0 7.99983 0 7.99983C0 7.99983 0 10.6032 0.334336 11.851C0.425545 12.19 0.604226 12.4992 0.852487 12.7474C1.10075 12.9957 1.40988 13.1744 1.74892 13.2656C2.99672 13.5999 7.99992 13.5999 7.99992 13.5999C7.99992 13.5999 13.0033 13.5999 14.2511 13.2656C14.5901 13.1744 14.8992 12.9957 15.1475 12.7474C15.3958 12.4992 15.5744 12.19 15.6657 11.851C16 10.6032 16 7.99983 16 7.99983C16 7.99983 16 5.39662 15.6657 4.14882Z"
                      fill="currentColor"
                    />
                    <path
                      d="M6.40027 10.4001L10.557 8.00025L6.40027 5.6001V10.4001Z"
                      fill="white"
                    />
                  </svg>
                  Upload to Youtube
                </ActionsDropdownButton>
                <ActionsDropdownButton
                  as="button"
                  onClick={() =>
                    window.open(compilation.video?.versions.original, '_blank')
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.65643 3.53061L9.1881 1.99843C9.50412 1.682 9.87938 1.43096 10.2924 1.25965C10.7055 1.08833 11.1482 1.0001 11.5953 1C12.0425 0.999896 12.4852 1.08792 12.8984 1.25904C13.3115 1.43016 13.6869 1.68103 14.003 1.99731C14.3192 2.31359 14.57 2.68908 14.741 3.10234C14.9121 3.5156 15.0001 3.95853 15 4.40582C14.9999 4.8531 14.9117 5.29599 14.7404 5.70917C14.5692 6.12235 14.3182 6.49773 14.0019 6.81386L11.8138 9.00269C11.4977 9.31888 11.1225 9.56969 10.7095 9.74081C10.2965 9.91193 9.85391 10 9.4069 10C8.9599 10 8.51727 9.91193 8.1043 9.74081C7.69132 9.56969 7.31608 9.31888 7 9.00269"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.34357 12.4694L6.8119 14.0016C6.49588 14.318 6.12062 14.569 5.70758 14.7404C5.29454 14.9117 4.85181 14.9999 4.40467 15C3.95753 15.0001 3.51476 14.9121 3.10164 14.741C2.68852 14.5698 2.31315 14.319 1.99697 14.0027C1.6808 13.6864 1.43002 13.3109 1.25895 12.8977C1.08789 12.4844 0.999896 12.0415 1 11.5942C1.0001 11.1469 1.0883 10.704 1.25956 10.2908C1.43082 9.87765 1.68177 9.50227 1.99809 9.18614L4.18619 6.99731C4.50227 6.68112 4.87751 6.43031 5.29049 6.25919C5.70346 6.08807 6.14609 6 6.59309 6C7.0401 6 7.48272 6.08807 7.8957 6.25919C8.30868 6.43031 8.68392 6.68112 9 6.99731"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Open file link
                </ActionsDropdownButton>
              </>
            )}
            {!!compilation.fileData && (
              <ActionsDropdownButton
                as="button"
                onClick={() =>
                  window.open(compilation.fileData?.link, '_blank')
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.65643 3.53061L9.1881 1.99843C9.50412 1.682 9.87938 1.43096 10.2924 1.25965C10.7055 1.08833 11.1482 1.0001 11.5953 1C12.0425 0.999896 12.4852 1.08792 12.8984 1.25904C13.3115 1.43016 13.6869 1.68103 14.003 1.99731C14.3192 2.31359 14.57 2.68908 14.741 3.10234C14.9121 3.5156 15.0001 3.95853 15 4.40582C14.9999 4.8531 14.9117 5.29599 14.7404 5.70917C14.5692 6.12235 14.3182 6.49773 14.0019 6.81386L11.8138 9.00269C11.4977 9.31888 11.1225 9.56969 10.7095 9.74081C10.2965 9.91193 9.85391 10 9.4069 10C8.9599 10 8.51727 9.91193 8.1043 9.74081C7.69132 9.56969 7.31608 9.31888 7 9.00269"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.34357 12.4694L6.8119 14.0016C6.49588 14.318 6.12062 14.569 5.70758 14.7404C5.29454 14.9117 4.85181 14.9999 4.40467 15C3.95753 15.0001 3.51476 14.9121 3.10164 14.741C2.68852 14.5698 2.31315 14.319 1.99697 14.0027C1.6808 13.6864 1.43002 13.3109 1.25895 12.8977C1.08789 12.4844 0.999896 12.0415 1 11.5942C1.0001 11.1469 1.0883 10.704 1.25956 10.2908C1.43082 9.87765 1.68177 9.50227 1.99809 9.18614L4.18619 6.99731C4.50227 6.68112 4.87751 6.43031 5.29049 6.25919C5.70346 6.08807 6.14609 6 6.59309 6C7.0401 6 7.48272 6.08807 7.8957 6.25919C8.30868 6.43031 8.68392 6.68112 9 6.99731"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Google Drive
              </ActionsDropdownButton>
            )}
            {compilation.status !==
              VideoCompilerCompilationStatus.IN_PROCESS && (
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <TrashIcon />
                Delete
              </ActionsDropdownButton>
            )}
          </StyledActionsDropdown>
          <AnimatePresence>
            {showYoutubeForm && (
              <YoutubeVideoForm
                onClose={() => setShowYoutubeForm(false)}
                compilationId={compilation.id}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default CompilationsTableRow;
